import type { RetrievedChunkSnapshot } from "../db/types";
import { tr } from "../i18n/i18n.svelte";

// Exa's hosted MCP server (https://exa.ai). It's an MCP "Streamable HTTP"
// endpoint, not a plain REST API, so we drive it with the minimal JSON-RPC
// handshake: initialize -> notifications/initialized -> tools/call. It needs no
// API key and serves wide-open CORS, so the extension/SPA can call it directly
// (same direct-fetch model as the EPFL hybrid endpoint).
const EXA_MCP_URL = "https://mcp.exa.ai/mcp";
const PROTOCOL_VERSION = "2024-11-05";

async function postMcp(
  body: unknown,
  sessionId: string | null,
  signal?: AbortSignal,
): Promise<Response> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    // The server replies either as JSON or as an SSE stream; accept both.
    Accept: "application/json, text/event-stream",
  };
  if (sessionId) {
    headers["Mcp-Session-Id"] = sessionId;
    headers["MCP-Protocol-Version"] = PROTOCOL_VERSION;
  }
  return fetch(EXA_MCP_URL, {
    method: "POST",
    headers,
    body: JSON.stringify(body),
    signal,
  });
}

// MCP responses come back as Server-Sent Events (`event: message\ndata: {...}`)
// or, occasionally, plain JSON. Both are one-shot here (the server closes after
// the reply), so reading the whole body and pulling the first result is enough.
function extractJsonRpc(text: string, contentType: string): any | null {
  if (contentType.includes("application/json")) {
    try {
      return JSON.parse(text);
    } catch {
      return null;
    }
  }
  for (const line of text.split(/\r?\n/)) {
    const trimmed = line.trim();
    if (!trimmed.startsWith("data:")) continue;
    const payload = trimmed.slice(5).trim();
    if (!payload || payload === "[DONE]") continue;
    try {
      const obj = JSON.parse(payload);
      if (obj && (obj.result !== undefined || obj.error !== undefined)) return obj;
    } catch {
      // partial / non-JSON data line; skip
    }
  }
  return null;
}

interface ExaResult {
  title: string;
  url: string;
  text: string;
}

// Exa's web_search_exa returns a single human-readable text blob: one block per
// result, separated by a `---` line, each starting with `Title:` / `URL:` /
// `Published:` / `Author:` then `Highlights:` followed by the excerpt (with
// `[...]` between non-contiguous fragments). Parse it into per-result records.
// Turn Exa's `[...]` fragment separators into ellipses (the citation
// highlighter already splits quotes on ellipsis) and trim.
function cleanFragment(s: string): string {
  return s.replace(/\s*\[\.\.\.\]\s*/g, " … ").trim();
}

function parseExaBlob(blob: string): ExaResult[] {
  const out: ExaResult[] = [];
  // Each result block begins with a `Title:` line. We split on `---` separators,
  // but page content itself can contain `---` (markdown rules), so a block that
  // does NOT start with `Title:` is a continuation of the previous result and is
  // merged back into it rather than counted as a spurious extra result.
  for (const raw of blob.split(/\n\s*-{3,}\s*\n/)) {
    const block = raw.trim();
    if (!block) continue;
    if (!/^Title:/.test(block)) {
      if (out.length > 0) out[out.length - 1].text += " … " + cleanFragment(block);
      continue;
    }
    const titleM = block.match(/^Title:\s*(.*)$/m);
    const urlM = block.match(/^URL:\s*(.*)$/m);
    const hlIdx = block.indexOf("Highlights:");
    const text = cleanFragment(
      hlIdx >= 0 ? block.slice(hlIdx + "Highlights:".length) : block,
    );
    const url = (urlM?.[1] ?? "").trim();
    let title = (titleM?.[1] ?? "").trim();
    if (!title || title === "N/A") title = url || tr({ fr: "Résultat web", en: "Web result" });
    if (!text) continue;
    out.push({ title, url, text });
  }
  return out;
}

function toSnapshot(r: ExaResult, i: number): RetrievedChunkSnapshot {
  const key = r.url || `exa:${i}`;
  return {
    number: 0, // chat.ts renumbers the merged corpus before prompting
    docId: key,
    chunkId: key,
    docName: r.title,
    page: 0,
    section: null,
    text: r.text,
    score: 0,
    source: "web",
    sourceUrl: r.url || undefined,
  };
}

/** Search the web via Exa's hosted MCP server and map the results to the local
 *  snapshot shape. Returns `[]` on any failure (handshake/network/non-200/parse)
 *  so retrieval degrades gracefully — web search is a supplement, never a hard
 *  dependency. Aborts propagate so the caller's Promise.all unwinds. */
export async function searchExaWeb(
  query: string,
  numResults: number,
  signal?: AbortSignal,
): Promise<RetrievedChunkSnapshot[]> {
  try {
    // 1. initialize — the session id comes back in a response header.
    const initRes = await postMcp(
      {
        jsonrpc: "2.0",
        id: 1,
        method: "initialize",
        params: {
          protocolVersion: PROTOCOL_VERSION,
          capabilities: {},
          clientInfo: { name: "dpo-agent", version: "1" },
        },
      },
      null,
      signal,
    );
    if (!initRes.ok) {
      console.warn(`Exa MCP initialize failed: ${initRes.status}`);
      return [];
    }
    const sessionId = initRes.headers.get("mcp-session-id");
    await initRes.text(); // drain the body to free the connection

    // 2. initialized notification (best-effort; spec requires it before calls).
    if (sessionId) {
      await postMcp(
        { jsonrpc: "2.0", method: "notifications/initialized", params: {} },
        sessionId,
        signal,
      )
        .then((r) => r.text())
        .catch(() => {});
    }

    // 3. tools/call web_search_exa.
    const callRes = await postMcp(
      {
        jsonrpc: "2.0",
        id: 2,
        method: "tools/call",
        params: { name: "web_search_exa", arguments: { query, numResults } },
      },
      sessionId,
      signal,
    );
    if (!callRes.ok) {
      console.warn(`Exa MCP tools/call failed: ${callRes.status}`);
      return [];
    }
    const msg = extractJsonRpc(
      await callRes.text(),
      callRes.headers.get("content-type") ?? "",
    );
    if (!msg || msg.error) return [];
    const content: Array<{ type: string; text?: string }> = msg.result?.content ?? [];
    const textPart = content.find((c) => c.type === "text" && c.text);
    if (!textPart?.text) return [];
    return parseExaBlob(textPart.text).map(toSnapshot);
  } catch (e) {
    if (e instanceof DOMException && e.name === "AbortError") throw e;
    console.warn("Exa web search error:", e);
    return [];
  }
}
